import { createTheme, Theme } from '@mui/material/styles';

export function generateTheme(primaryColor: string, mode: 'light' | 'dark'): Theme {
  const isLight = mode === 'light';

  return createTheme({
    palette: {
      mode,
      primary: {
        main: primaryColor,
        light: isLight ? adjustColor(primaryColor, 40) : adjustColor(primaryColor, -20),
        dark: isLight ? adjustColor(primaryColor, -20) : adjustColor(primaryColor, 20),
        contrastText: isLight ? '#FFFFFF' : '#000000',
      },
      secondary: {
        main: isLight ? '#625B71' : '#CCC2DC',
        light: isLight ? '#E8DEF8' : '#4A4458',
        dark: isLight ? '#4A4458' : '#625B71',
        contrastText: isLight ? '#FFFFFF' : '#332D41',
      },
      error: {
        main: isLight ? '#B3261E' : '#F2B8B5',
        light: isLight ? '#F9DEDC' : '#8C1D18',
        dark: isLight ? '#8C1D18' : '#F9DEDC',
        contrastText: isLight ? '#FFFFFF' : '#601410',
      },
      warning: {
        main: isLight ? '#7D5260' : '#FFB4A9',
        light: isLight ? '#FFD8E4' : '#633B48',
        dark: isLight ? '#633B48' : '#FFD8E4',
      },
      success: {
        main: isLight ? '#386A20' : '#9CD67D',
        light: isLight ? '#B8F397' : '#1F3700',
        dark: isLight ? '#1F3700' : '#B8F397',
      },
      background: {
        default: isLight ? '#FFFBFE' : '#1C1B1F',
        paper: isLight ? '#FFFBFE' : '#2B2930',
      },
      text: {
        primary: isLight ? '#1C1B1F' : '#E6E1E5',
        secondary: isLight ? '#49454F' : '#CAC4D0',
      },
      divider: isLight ? '#CAC4D0' : '#49454F',
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      h1: { fontSize: '2.5rem', fontWeight: 400 },
      h2: { fontSize: '2rem', fontWeight: 400 },
      h3: { fontSize: '1.75rem', fontWeight: 400 },
      h4: { fontSize: '1.5rem', fontWeight: 500 },
      h5: { fontSize: '1.25rem', fontWeight: 500 },
      h6: { fontSize: '1rem', fontWeight: 500 },
      body1: { fontSize: '1rem', lineHeight: 1.5 },
      body2: { fontSize: '0.875rem', lineHeight: 1.43 },
      button: {
        textTransform: 'none',
        fontWeight: 500,
      },
    },
    shape: {
      borderRadius: 12,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: `
          :root {
            color-scheme: ${isLight ? 'light' : 'dark'};
          }
          /* Invisible scrollbars that appear on hover */
          * {
            scrollbar-width: thin;
            scrollbar-color: transparent transparent;
          }
          *:hover {
            scrollbar-color: ${isLight ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.2)'} transparent;
          }
          *::-webkit-scrollbar {
            width: 6px;
            height: 6px;
          }
          *::-webkit-scrollbar-track {
            background: transparent;
          }
          *::-webkit-scrollbar-thumb {
            background: transparent;
            border-radius: 3px;
          }
          *:hover::-webkit-scrollbar-thumb {
            background: ${isLight ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.2)'};
          }
          /* Force 1px borders globally to prevent sub-pixel rendering
             on Windows with non-100% DPI scaling (125%, 150%).
             MUI uses the CSS 'thin' keyword internally which Chrome
             interprets as 1/devicePixelRatio (e.g. 0.667px at 150%). */
          .MuiDivider-root {
            border-bottom-width: 1px !important;
            border-right-width: 1px !important;
          }
          .MuiOutlinedInput-notchedOutline {
            border-width: 1px !important;
          }
          .MuiButton-outlined {
            border-width: 1px !important;
          }
          .MuiChip-outlined {
            border-width: 1px !important;
          }
          .MuiAlert-outlined {
            border-width: 1px !important;
          }
          .MuiPaper-outlined {
            border-width: 1px !important;
          }
          .MuiAccordion-root {
            border-width: 1px !important;
          }
          .MuiTableCell-root {
            border-bottom-width: 1px !important;
          }
          .MuiListItem-divider {
            border-bottom-width: 1px !important;
          }
        `,
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 20,
            padding: '8px 24px',
          },
          contained: {
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: isLight
              ? '0px 1px 2px rgba(0, 0, 0, 0.3), 0px 1px 3px 1px rgba(0, 0, 0, 0.15)'
              : '0px 1px 2px rgba(0, 0, 0, 0.6), 0px 1px 3px 1px rgba(0, 0, 0, 0.3)',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 12,
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 8,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 28,
          },
        },
      },
      MuiFab: {
        styleOverrides: {
          root: {
            borderRadius: 16,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRadius: 0,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
          },
        },
      },
    },
  });
}

function adjustColor(hex: string, percent: number): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.min(255, Math.max(0, (num >> 16) + amt));
  const G = Math.min(255, Math.max(0, ((num >> 8) & 0x00ff) + amt));
  const B = Math.min(255, Math.max(0, (num & 0x0000ff) + amt));
  return `#${((1 << 24) + (R << 16) + (G << 8) + B).toString(16).slice(1)}`;
}

export const DEFAULT_PRIMARY_COLOR = '#6750A4';

export const COLOR_PRESETS = [
  { name: 'Purple', color: '#9C27B0' },
  { name: 'Indigo', color: '#5C6BC0' },
  { name: 'Blue', color: '#2196F3' },
  { name: 'Teal', color: '#26A69A' },
  { name: 'Green', color: '#4CAF50' },
  { name: 'Orange', color: '#FF9800' },
  { name: 'Red', color: '#F44336' },
  { name: 'Pink', color: '#EC407A' },
];

export const ITEM_COLORS = [
  '#9C27B0', // Purple
  '#5C6BC0', // Indigo
  '#2196F3', // Blue
  '#00BCD4', // Cyan
  '#26A69A', // Teal
  '#4CAF50', // Green
  '#8BC34A', // Light Green
  '#FF9800', // Orange
  '#FF5722', // Deep Orange
  '#F44336', // Red
  '#EC407A', // Pink
  '#78909C', // Blue Grey
];
